import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { notifyCouple } from '../../lib/notifications'

const CURRENCY_SYMBOLS = { NGN:'₦', USD:'$', GBP:'£', EUR:'€', GHS:'₵', KES:'KSh', ZAR:'R' }

export default function AsoEbiGuestView({ asoEbi, asoEbiPhotos, weddingId, wedding, daysLeft, isClosed }) {
  // Derive currency symbol from the couple's wedding settings
  const cs = CURRENCY_SYMBOLS[wedding?.currency] || '₦'
  const [step, setStep]               = useState('browse') // browse | order | order_more | receipt
  const [orderData, setOrderData]     = useState({ name:'', phone:'', yards:asoEbi?.min_yards||1, isGroup:false, groupCount:1, groupNames:[], collectionPoint:'' })
  const [collectionPoints, setCollectionPoints] = useState([])
  const [receipt, setReceipt]         = useState(null)
  const [submitting, setSubmitting]   = useState(false)
  const [refCopied, setRefCopied]     = useState(false)
  const [lightbox, setLightbox]       = useState(null)
  const [orderCount, setOrderCount]   = useState(null)
  const [purchases, setPurchases]     = useState([])   // this guest's orders for this wedding (localStorage)
  const [showOrderDetails, setShowOrderDetails] = useState(false)
  const [fullOrder, setFullOrder]     = useState(null) // fetched from Supabase
  const [paying, setPaying]           = useState(false)
  const [payError, setPayError]       = useState('')
  const [paidBanner, setPaidBanner]   = useState(false)
  const [feePct, setFeePct]           = useState(0.04)
  const [feeCap, setFeeCap]           = useState(5000)
  const [feeFloor, setFeeFloor]       = useState(0)

  const giftsOn = !!wedding?.gifts_enabled  // couple has a Paystack payout account → online pay available
  const deadlinePassed = asoEbi?.order_deadline && new Date() > new Date(asoEbi.order_deadline)

  // Derived: the guest may have several orders; the receipt accumulates the paid ones.
  function savePurchases(next){ setPurchases(next); try { localStorage.setItem(`asoebi_purchases_${weddingId}`, JSON.stringify(next)) } catch(_){} }
  const existingOrder   = purchases.length ? purchases[purchases.length - 1] : null
  const paidPurchases   = purchases.filter(p => p.status === 'paid' || p.status === 'collected')
  const pendingPurchase = purchases.find(p => !(p.status === 'paid' || p.status === 'collected'))
  const accYards  = paidPurchases.reduce((s,p)=>s+Number(p.yards||0),0)
  const accAmount = paidPurchases.reduce((s,p)=>s+Number(p.amount||0),0)
  const accPoints = [...new Set(paidPurchases.map(p=>p.point).filter(Boolean))]
  const anyPaid   = paidPurchases.length > 0

  // Load the guest's purchases from localStorage (migrating the old single-order key)
  useEffect(() => {
    try {
      let list = JSON.parse(localStorage.getItem(`asoebi_purchases_${weddingId}`) || 'null')
      if (!list) {
        const old = JSON.parse(localStorage.getItem(`asoebi_order_${weddingId}`) || 'null')
        list = old ? [{ ...old, status: old.status || 'pending' }] : []
        if (list.length) localStorage.setItem(`asoebi_purchases_${weddingId}`, JSON.stringify(list))
      }
      if (list.length) setPurchases(list)
    } catch(e) {}

    supabase.from('asoebi_collection_points').select('*').eq('wedding_id', weddingId)
      .then(({ data }) => setCollectionPoints(data||[]))

    supabase.rpc('count_asoebi_orders', { p_wedding_id: weddingId })
      .then(({ data }) => setOrderCount(data || 0))

    // Fee config so the breakdown matches what the server will charge
    supabase.from('admin_settings').select('value').eq('key','asoebi_fee').maybeSingle()
      .then(({ data }) => {
        const p = Number(data?.value?.pct); const c = Number(data?.value?.cap); const f = Number(data?.value?.floor)
        if (p >= 0 && p <= 50) setFeePct(p / 100)
        setFeeCap(c > 0 ? c : Infinity)
        if (f > 0) setFeeFloor(f)
      }).catch(()=>{})
  }, [weddingId])

  // Keep any still-pending purchases in sync with the server (covers a payment
  // made on another device, or localStorage that's behind).
  useEffect(() => {
    const pend = purchases.filter(p => !(p.status === 'paid' || p.status === 'collected'))
    if (!pend.length) return
    Promise.all(pend.map(p => supabase.rpc('get_asoebi_order', { p_ref: p.ref }).then(({ data }) => ({ ref: p.ref, status: data?.status })).catch(()=>({ ref:p.ref }))))
      .then(results => {
        let changed = false
        const next = purchases.map(p => {
          const r = results.find(x => x.ref === p.ref)
          if (r?.status && r.status !== p.status) { changed = true; return { ...p, status: r.status } }
          return p
        })
        if (changed) savePurchases(next)
      })
  }, [purchases.length])

  // After returning from Paystack (?asoebi=<ref>), confirm the payment
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get('asoebi')
    if (!ref) return
    fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-asoebi`, {
      method:'POST',
      headers:{ 'Content-Type':'application/json', apikey:import.meta.env.VITE_SUPABASE_ANON_KEY, Authorization:`Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}` },
      body: JSON.stringify({ reference: ref }),
    }).then(r=>r.json()).then(j=>{
      if (!j.ok) return
      setPaidBanner(true)
      // Mark (or add) this order as paid in the guest's purchase list, using the
      // server-confirmed details so the receipt is right even cross-device.
      try {
        const list = JSON.parse(localStorage.getItem(`asoebi_purchases_${weddingId}`) || '[]')
        const entry = { ref: j.receipt_ref, yards: j.yards, amount: j.amount, point: j.collection_point, status: 'paid' }
        const idx = j.receipt_ref ? list.findIndex(p => p.ref === j.receipt_ref) : -1
        const next = idx >= 0 ? list.map((p,i)=> i===idx ? { ...p, ...entry } : p) : [...list, entry]
        setPurchases(next); localStorage.setItem(`asoebi_purchases_${weddingId}`, JSON.stringify(next))
      } catch(_){}
    }).catch(()=>{})
    try { const u = new URL(window.location.href); u.searchParams.delete('asoebi'); window.history.replaceState({}, '', u.toString()) } catch(_){}
  }, [])

  // Kick off an online card/transfer payment for an existing order
  async function payOnline(ref) {
    if (!ref) return
    setPaying(true); setPayError('')
    try {
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/asoebi-init`, {
        method:'POST',
        headers:{ 'Content-Type':'application/json', apikey:import.meta.env.VITE_SUPABASE_ANON_KEY, Authorization:`Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}` },
        body: JSON.stringify({ wedding_id: weddingId, receipt_ref: ref, origin: window.location.origin }),
      })
      const j = await res.json()
      if (!j.ok || !j.authorization_url) { setPayError(j.error || 'Could not start the payment.'); setPaying(false); return }
      window.location.href = j.authorization_url
    } catch(_) { setPayError('Something went wrong. Please try again.'); setPaying(false) }
  }

  // Reusable online-pay card: transparent breakdown + button
  function OnlinePayBlock({ amount, orderRef }) {
    const a = Number(amount) || 0
    let fee = Math.min(Math.round(a * feePct), feeCap === Infinity ? Infinity : Math.round(feeCap))
    if (feeFloor > 0) fee = Math.max(fee, Math.round(feeFloor))
    const total = a + fee
    return (
      <div style={{ background:'linear-gradient(135deg,#FAF5EC,#F5EAD8)', border:'1px solid rgba(196,130,106,0.25)', borderRadius:'1.25rem', padding:'1.25rem' }}>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.85rem', color:'#1A1A1A', margin:'0 0 0.875rem' }}>💳 Pay securely online</p>
        <div style={{ display:'flex', flexDirection:'column', gap:'0.3rem', marginBottom:'0.875rem' }}>
          {[
            { l:'Fabric', v:a },
            { l:'Service fee', v:fee },
          ].map(r=>(
            <div key={r.l} style={{ display:'flex', justifyContent:'space-between', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', color:'#6B6B6B' }}>
              <span>{r.l}</span><span style={{ fontWeight:600, color:'#1A1A1A' }}>{cs}{r.v.toLocaleString()}</span>
            </div>
          ))}
          <div style={{ borderTop:'1px solid rgba(196,130,106,0.2)', paddingTop:'0.35rem', display:'flex', justifyContent:'space-between', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.88rem', fontWeight:800, color:'#1A1A1A' }}>
            <span>You pay</span><span>{cs}{total.toLocaleString()}</span>
          </div>
        </div>
        {payError && <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#DC2626', margin:'0 0 0.5rem' }}>{payError}</p>}
        <button onClick={()=>payOnline(orderRef)} disabled={paying} style={{ width:'100%', background:'linear-gradient(135deg,#C4826A,#F4B300)', color:'white', border:'none', borderRadius:100, padding:'0.8rem', cursor:'pointer', fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.875rem', opacity:paying?0.7:1 }}>
          {paying ? 'Starting…' : `Pay ${cs}${total.toLocaleString()} →`}
        </button>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', color:'#9B9B9B', margin:'0.5rem 0 0', textAlign:'center' }}>
          🔒 Secured by Paystack · the couple receives the full {cs}{a.toLocaleString()}
        </p>
      </div>
    )
  }

  // Fetch full order details from Supabase (via security-definer RPC)
  async function loadFullOrder(ref) {
    if (!ref) return
    const { data } = await supabase.rpc('get_asoebi_order', { p_ref: ref })
    if (data) setFullOrder(data)
    setShowOrderDetails(true)
  }

  // CSV download for collection point card
  function downloadCollectionCard(pt, receipt) {
    const lines = [
      `COLLECTION POINT`,
      ``,
      `Location:   ${pt.name}`,
      pt.address   ? `Address:    ${pt.address}` : null,
      pt.contact_person ? `Contact:    ${pt.contact_person}` : null,
      pt.contact_phone  ? `Phone:      ${pt.contact_phone}` : null,
      pt.available_dates ? `Available:  ${pt.available_dates}` : null,
      ``,
      `YOUR ORDER`,
      `Name:       ${receipt.name}`,
      `Yards:      ${receipt.yards}`,
      `Amount:     ${cs}${receipt.amount.toLocaleString()}`,
      `Reference:  ${receipt.ref}`,
    ].filter(l => l !== null).join('\n')
    const blob = new Blob([lines], { type:'text/plain;charset=utf-8;' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href     = url
    a.setAttribute('download', `${pt.name.replace(/\s+/g,'-').toLowerCase()}-collection-point.txt`)
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(url), 100)
  }

  const totalAmount = Number(asoEbi?.price_per_yard||0) * Number(orderData.yards||0) * (orderData.isGroup ? Number(orderData.groupCount||1) : 1)

  async function submitOrder() {
    if (!orderData.name.trim() || !orderData.phone.trim() || !orderData.yards) return
    setSubmitting(true)
    const yards = Number(orderData.yards) * (orderData.isGroup ? Number(orderData.groupCount||1) : 1)

    // Every order is its own record + its own payment (so "order more" after
    // paying is a fresh purchase that simply adds to the running receipt).
    const ref = `OKY-${Math.random().toString(36).slice(2,8).toUpperCase()}`
    const { error } = await supabase.from('asoebi_orders').insert({
      wedding_id: weddingId,
      guest_name: orderData.name.trim(),
      guest_phone: orderData.phone.trim()||null,
      yards, amount:totalAmount, status:'pending',
      collection_point: orderData.collectionPoint||null,
      receipt_ref: ref,
      is_group: orderData.isGroup,
      group_names: orderData.isGroup ? orderData.groupNames.filter(Boolean) : [],
    })

    if (!error) {
      const orderInfo = { ref, name:orderData.name, phone:orderData.phone, yards, amount:totalAmount, point:orderData.collectionPoint, status:'pending' }
      setReceipt(orderInfo)
      savePurchases([...purchases, orderInfo])
      notifyCouple(weddingId, '🧢 New Aso Ebi Order!', `${orderData.name.trim()} ordered ${yards} yard${yards !== 1 ? 's' : ''}`, 'asoebi')
      setStep('receipt')
      setOrderCount(c => (c||0)+1)
    }
    setSubmitting(false)
  }


  // ── Payment details block ─────────────────────────────────
  function PaymentDetailsBlock({ wedding, order, fullOrder, existingOrder, asoEbi }) {
    const ref     = fullOrder?.receipt_ref || existingOrder?.ref
    const waPhone = asoEbi?.vendor_contact?.replace(/[\s\-+()\u200B]/g,'').replace(/^0/,'234')
    const msg     = `Hi ${asoEbi?.vendor_name||'there'}, I've made my Aso Ebi payment.\n\nReference: ${ref}\nAmount: ${cs}${Number(order?.amount||0).toLocaleString()}\n\nPlease find my transaction receipt attached.`
    const waUrl   = waPhone ? `https://wa.me/${waPhone}?text=${encodeURIComponent(msg)}` : null
    return (
      <div style={{ marginTop:'0.25rem', background:'rgba(244,179,0,0.06)', border:'1px solid rgba(244,179,0,0.2)', borderRadius:'0.875rem', padding:'1rem' }}>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.78rem', color:'#1A1A1A', margin:'0 0 0.625rem' }}>💳 Payment Details</p>
        {[
          { label:'Bank',         value:wedding?.bank_name },
          { label:'Account Name', value:wedding?.bank_account_name },
          { label:'Account No.',  value:wedding?.bank_account_number },
          { label:'Amount',       value:order?.amount ? `${cs}${Number(order.amount).toLocaleString()}` : null },
          { label:'Reference',    value:ref },
        ].filter(r=>r.value).map(r=>(
          <div key={r.label} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'0.3rem 0', borderBottom:'1px solid rgba(244,179,0,0.1)' }}>
            <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#9B9B9B' }}>{r.label}</span>
            <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#1A1A1A', fontWeight:600 }}>{r.value}</span>
          </div>
        ))}
        {waUrl && (
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#6B6B6B', margin:'0.625rem 0 0', lineHeight:1.6 }}>
            When payment is completed, send your receipt to{' '}
            <a href={waUrl} target="_blank" rel="noreferrer" style={{ color:'#25D366', fontWeight:700, textDecoration:'none' }}>{asoEbi?.vendor_name||'the coordinator'} on WhatsApp →</a>
          </p>
        )}
      </div>
    )
  }

  // ── Null guard — no aso ebi set up yet ─────────────────
  if (!asoEbi) return (
    <div style={{padding:'3rem 1.5rem',textAlign:'center'}}>
      <p style={{fontFamily:'Gibs, serif',fontSize:'1.1rem',color:'#1A1A1A',margin:'0 0 0.5rem'}}>Aso Ebi</p>
      <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.82rem',color:'#9B9B9B',lineHeight:1.6}}>The couple hasn't set up Aso Ebi details yet. Check back soon!</p>
    </div>
  )

  // ── Lightbox ──────────────────────────────────────────────
  if (lightbox) return (
    <div onClick={()=>setLightbox(null)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.93)', zIndex:200, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'1rem' }}>
      <button onClick={()=>setLightbox(null)} style={{ position:'absolute', top:16, right:16, background:'rgba(255,255,255,0.12)', border:'none', borderRadius:'50%', width:40, height:40, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:'white', fontSize:'1.2rem' }}>✕</button>
      <img src={lightbox.url} alt={lightbox.label} onClick={e=>e.stopPropagation()} style={{ maxWidth:'100%', maxHeight:'88vh', objectFit:'contain', borderRadius:'0.75rem' }}/>
      {lightbox.label && <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'rgba(255,255,255,0.5)', marginTop:'0.75rem' }}>{lightbox.label}</p>}
    </div>
  )

  // ── Order Details Modal ───────────────────────────────────
  if (showOrderDetails) {
    const pt = collectionPoints.find(p => p.name === (fullOrder?.collection_point || existingOrder?.point))
    const mapsUrl = pt?.latitude && pt?.longitude
      ? `https://www.google.com/maps/dir/?api=1&destination=${pt.latitude},${pt.longitude}`
      : pt?.address
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(pt.address)}`
      : null

    const isMobile = window.innerWidth < 768
    const order = fullOrder || existingOrder

    const detailContent = (
      <div style={{ display:'flex', flexDirection:'column', height:'100%' }}>
        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'1.25rem 1.5rem', borderBottom:'1px solid #F3F0EA', flexShrink:0 }}>
          <p style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.1rem', color:'#1A1A1A', margin:0 }}>Your Aso Ebi Order</p>
          <button onClick={()=>setShowOrderDetails(false)} style={{ background:'none', border:'none', cursor:'pointer', color:'#9B9B9B', padding:4 }}>✕</button>
        </div>
        {/* Body */}
        <div style={{ overflowY:'auto', maxHeight:'calc(80vh - 130px)', padding:'1.25rem 1.5rem', display:'flex', flexDirection:'column', gap:'0.875rem' }}>
          {[
            { label:'Fabric',        value:asoEbi?.fabric_name },
            { label:'Yards',         value:order?.yards ? `${order.yards} yds` : null },
            { label:'Amount',        value:order?.amount ? `${cs}${Number(order.amount).toLocaleString()}` : null },
            { label:'Status',        value:fullOrder?.status || 'Pending payment' },
            { label:'Collection',    value:order?.point || existingOrder?.point },
            pt?.address ? { label:'Address', value:pt.address } : null,
            pt?.contact_person ? { label:'Contact', value:`${pt.contact_person}${pt.contact_phone ? ' · ' + pt.contact_phone : ''}` } : null,
          ].filter(Boolean).map(r=>(
            <div key={r.label} style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', padding:'0.4rem 0', borderBottom:'1px solid #F3F0EA', gap:'0.5rem' }}>
              <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#9B9B9B', flexShrink:0 }}>{r.label}</span>
              <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#1A1A1A', fontWeight:600, textAlign:'right' }}>{r.value || '—'}</span>
            </div>
          ))}
          {mapsUrl && (
            <a href={mapsUrl} target="_blank" rel="noreferrer" style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'0.4rem', background:'rgba(196,130,106,0.08)', border:'1px solid rgba(196,130,106,0.2)', borderRadius:'0.875rem', padding:'0.75rem', textDecoration:'none', fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.82rem', color:'#C4826A' }}>
              📍 Get Directions →
            </a>
          )}

          {/* Payment details — bank transfer only when online payment isn't on */}
          {!giftsOn && (wedding?.bank_account_name||wedding?.bank_account_number) && (<PaymentDetailsBlock
            wedding={wedding} order={order} fullOrder={fullOrder} existingOrder={existingOrder} asoEbi={asoEbi}
          />)}
        </div>
        {/* Footer */}
        <div style={{ padding:'1rem 1.5rem', borderTop:'1px solid #F3F0EA', flexShrink:0 }}>
          <button onClick={()=>setShowOrderDetails(false)} style={{ width:'100%', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.85rem', fontWeight:700, color:'#6B6B6B', background:'#F3F0EA', border:'none', borderRadius:100, padding:'0.75rem', cursor:'pointer' }}>Close</button>
        </div>
      </div>
    )

    return isMobile ? (
      <div style={{ position:'fixed', inset:0, zIndex:200 }}>
        <div onClick={()=>setShowOrderDetails(false)} style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.4)' }}/>
        <div onClick={e=>e.stopPropagation()} style={{ position:'absolute', bottom:0, left:0, right:0, background:'white', borderRadius:'1.25rem 1.25rem 0 0', maxHeight:'85vh', display:'flex', flexDirection:'column', overflow:'hidden' }}>
          <div style={{ display:'flex', justifyContent:'center', padding:'0.75rem 0 0' }}><div style={{ width:36, height:4, background:'#E5E0D8', borderRadius:100 }}/></div>
          {detailContent}
        </div>
      </div>
    ) : (
      <div onClick={()=>setShowOrderDetails(false)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:200, padding:'1rem' }}>
        <div onClick={e=>e.stopPropagation()} style={{ background:'white', borderRadius:'1.25rem', width:'100%', maxWidth:480, display:'flex', flexDirection:'column', boxShadow:'0 20px 60px rgba(0,0,0,0.2)', maxHeight:'85vh' }}>
          {detailContent}
        </div>
      </div>
    )
  }

  // ── Receipt ───────────────────────────────────────────────
  if (step==='receipt' && receipt) {
    const pt = collectionPoints.find(p => p.name === receipt.point)
    const waPhone = asoEbi.vendor_contact?.replace(/[\s\-+()\u200B]/g,'').replace(/^0/,'234')
    const receiptMsg = `Hi ${asoEbi.vendor_name||'there'}, I've made my Aso Ebi payment for ${wedding.partner1_name} & ${wedding.partner2_name}'s wedding.\n\nName: ${receipt.name}\nReference: ${receipt.ref}\nAmount: ${cs}${receipt.amount.toLocaleString()}\n\nPlease find my transaction receipt attached.`
    const waUrl = waPhone ? `https://wa.me/${waPhone}?text=${encodeURIComponent(receiptMsg)}` : null

    return (
      <div style={{display:'flex',flexDirection:'column',gap:'1.25rem'}}>

        {/* Order Submitted */}
        <div style={{background:'linear-gradient(135deg,#FAF5EC,#F5EAD8)',border:'1px solid rgba(196,130,106,0.25)',borderRadius:'1.25rem',padding:'1.5rem',textAlign:'center'}}>
          <span style={{fontSize:'2rem',display:'block',marginBottom:'0.5rem'}}>✅</span>
          <p style={{fontFamily:'Gibs, serif',fontWeight:400,fontSize:'1.3rem',color:'#1A1A1A',margin:'0 0 0.25rem'}}>Order Submitted!</p>
          <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.82rem',color:'#6B6B6B',margin:0,lineHeight:1.7}}>{giftsOn ? 'Your order has been received. Pay securely below to confirm it.' : 'Your order has been received. Complete your bank transfer to confirm it.'}</p>
        </div>

        {/* Online payment (preferred when the couple has it on) */}
        {giftsOn && <OnlinePayBlock amount={receipt.amount} orderRef={receipt.ref}/>}

        {/* Bank Transfer — fallback only when online payment isn't enabled */}
        {!giftsOn && (wedding.bank_account_name||wedding.bank_account_number) && (
          <div style={{background:'rgba(244,179,0,0.06)',border:'1px solid rgba(244,179,0,0.2)',borderRadius:'1.25rem',padding:'1.25rem'}}>
            <p style={{fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.85rem',color:'#1A1A1A',margin:'0 0 0.875rem'}}>💳 Pay via Bank Transfer</p>
            {[
              {label:'Bank',         value:wedding.bank_name,           copy:false},
              {label:'Account Name', value:wedding.bank_account_name,   copy:false},
              {label:'Account No.',  value:wedding.bank_account_number, copy:true},
              {label:'Amount',       value:`${cs}${receipt.amount.toLocaleString()}`, copy:false},
              {label:'Reference',    value:receipt.ref,                 copy:true},
            ].filter(r=>r.value).map(r=>(
              <div key={r.label} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'0.4rem 0',borderBottom:'1px solid rgba(244,179,0,0.12)'}}>
                <span style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.78rem',color:'#9B9B9B'}}>{r.label}</span>
                <div style={{display:'flex',alignItems:'center',gap:'0.4rem'}}>
                  <span style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.78rem',color:'#1A1A1A',fontWeight:600}}>{r.value}</span>
                  {r.copy && <button onClick={()=>{navigator.clipboard.writeText(r.value);setRefCopied(true);setTimeout(()=>setRefCopied(false),2000)}} style={{background:'none',border:'1px solid rgba(244,179,0,0.3)',borderRadius:6,padding:'2px 8px',cursor:'pointer',fontFamily:'Nunito Sans, sans-serif',fontSize:'0.62rem',fontWeight:700,color:refCopied?'#9BAF9B':'#C4826A'}}>{refCopied?'Copied!':'Copy'}</button>}
                </div>
              </div>
            ))}
            <div style={{marginTop:'0.875rem',display:'flex',flexDirection:'column',gap:'0.625rem'}}>
              <div style={{display:'flex',gap:'0.5rem',alignItems:'flex-start',background:'rgba(244,179,0,0.04)',borderRadius:'0.625rem',padding:'0.5rem 0.625rem'}}>
                <span style={{fontSize:'0.8rem',flexShrink:0}}>💡</span>
                <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.75rem',color:'#6B6B6B',margin:0,lineHeight:1.6}}>Use <strong>{receipt.ref}</strong> as the transaction description / narration in your bank app so your payment can be matched to your order.</p>
              </div>
              {waUrl && (
                <div style={{display:'flex',gap:'0.5rem',alignItems:'flex-start',background:'rgba(37,211,102,0.04)',borderRadius:'0.625rem',padding:'0.5rem 0.625rem'}}>
                  <span style={{fontSize:'0.8rem',flexShrink:0}}>📨</span>
                  <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.75rem',color:'#6B6B6B',margin:0,lineHeight:1.6}}>
                    After payment, send your transaction receipt to{' '}
                    <a href={waUrl} target="_blank" rel="noreferrer" style={{color:'#25D366',fontWeight:700,textDecoration:'none'}}>{asoEbi.vendor_name||'the coordinator'} on WhatsApp →</a>
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Collection Point */}
        {pt && (
          <div style={{background:'linear-gradient(135deg,#FAF5EC,#F5EAD8)',border:'1px solid rgba(196,130,106,0.2)',borderRadius:'1.25rem',padding:'1.25rem'}}>
            <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.65rem',fontWeight:700,color:'#C4826A',letterSpacing:'0.12em',textTransform:'uppercase',margin:'0 0 0.875rem'}}>📍 Your Collection Point</p>
            <p style={{fontFamily:'Gibs, serif',fontWeight:400,fontSize:'1.2rem',color:'#1A1A1A',margin:'0 0 0.75rem'}}>{pt.name}</p>
            {[
              {label:'Address',   value:pt.address},
              {label:'Contact',   value:pt.contact_person},
              {label:'Phone',     value:pt.contact_phone},
              {label:'Available', value:pt.available_dates},
            ].filter(r=>r.value).map(r=>(
              <div key={r.label} style={{display:'flex',gap:'0.5rem',marginBottom:'0.4rem'}}>
                <span style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.75rem',color:'#9B9B9B',minWidth:65}}>{r.label}</span>
                <span style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.75rem',color:'#1A1A1A',fontWeight:600,lineHeight:1.5}}>{r.value}</span>
              </div>
            ))}
            <div style={{marginTop:'0.75rem',display:'flex',gap:'0.5rem',alignItems:'flex-start',background:'rgba(196,130,106,0.05)',borderRadius:'0.625rem',padding:'0.5rem 0.625rem'}}>
              <span style={{fontSize:'0.8rem',flexShrink:0}}>📦</span>
              <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.75rem',color:'#6B6B6B',margin:0,lineHeight:1.6}}>Bring your reference number <strong>{receipt.ref}</strong> when picking up your fabric.</p>
            </div>
            <button onClick={()=>downloadCollectionCard(pt, receipt)}
              style={{marginTop:'1rem',width:'100%',fontFamily:'Nunito Sans, sans-serif',fontSize:'0.75rem',fontWeight:700,color:'#C4826A',background:'rgba(196,130,106,0.1)',border:'1px solid rgba(196,130,106,0.2)',borderRadius:'0.75rem',padding:'0.65rem',cursor:'pointer'}}>
              ↓ Save Collection Details
            </button>
          </div>
        )}
      </div>
    )
  }

  // ── Order form (new or add more) ──────────────────────────
  if (step==='order' || step==='order_more') {
    const isMore = step==='order_more'
    return (
      <div style={{display:'flex',flexDirection:'column',gap:'1.25rem'}}>
        <button onClick={()=>setStep('browse')} style={{background:'none',border:'none',color:'#C4826A',cursor:'pointer',fontFamily:'Nunito Sans, sans-serif',fontSize:'0.82rem',fontWeight:600,padding:0,textAlign:'left'}}>← Back</button>
        <div style={{background:'linear-gradient(135deg,#FAF5EC,#F5EAD8)',border:'1px solid rgba(196,130,106,0.2)',borderRadius:'1.25rem',padding:'1.25rem'}}>
          <p style={{fontFamily:'Gibs, serif',fontWeight:400,fontSize:'1.1rem',color:'#1A1A1A',margin:'0 0 0.2rem'}}>{isMore ? 'Add More Yards' : 'Place Your Order'}</p>
          <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.78rem',color:'#9B9B9B',margin:0}}>{asoEbi.fabric_name} · {cs}{Number(asoEbi.price_per_yard).toLocaleString()} per yard</p>
          {isMore && existingOrder && <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.75rem',color:'#C4826A',margin:'0.5rem 0 0',fontWeight:600}}>You have already ordered {existingOrder.yards} yards. This will be added to your existing order.</p>}
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:'0.75rem'}}>
          <div>
            <label style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.72rem',fontWeight:700,color:'#6B6B6B',textTransform:'uppercase',letterSpacing:'0.04em'}}>Your Full Name *</label>
            <input className="input-field" placeholder="e.g. Aunty Folake" value={orderData.name} onChange={e=>setOrderData(p=>({...p,name:e.target.value}))} style={{marginTop:'0.3rem'}} disabled={isMore}/>
          </div>
          <div>
            <label style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.72rem',fontWeight:700,color:'#6B6B6B',textTransform:'uppercase',letterSpacing:'0.04em'}}>WhatsApp Number *</label>
            <input className="input-field" placeholder="e.g. 08012345678" value={orderData.phone} onChange={e=>setOrderData(p=>({...p,phone:e.target.value}))} style={{marginTop:'0.3rem'}} disabled={isMore}/>
          </div>
          {!isMore && (
            <div style={{background:'rgba(196,130,106,0.05)',border:'1px solid rgba(196,130,106,0.15)',borderRadius:'0.875rem',padding:'0.875rem 1rem'}}>
              <label style={{display:'flex',alignItems:'center',gap:'0.5rem',cursor:'pointer'}}>
                <input type="checkbox" checked={orderData.isGroup} onChange={e=>setOrderData(p=>({...p,isGroup:e.target.checked}))} style={{accentColor:'#C4826A'}}/>
                <span style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.82rem',fontWeight:600,color:'#1A1A1A'}}>Ordering for a group</span>
              </label>
              {orderData.isGroup && (
                <div style={{marginTop:'0.75rem',display:'flex',flexDirection:'column',gap:'0.5rem'}}>
                  <input className="input-field" type="number" min={1} max={20} value={orderData.groupCount}
                    onChange={e=>{
                      const raw=e.target.value
                      if(raw===''){ setOrderData(p=>({...p,groupCount:''})); return }   // allow clearing to retype
                      const n=Math.min(20,Math.max(1,Math.floor(Number(raw)||1)))
                      setOrderData(p=>({...p,groupCount:n,groupNames:Array(n).fill('').map((_,i)=>p.groupNames[i]||'')}))
                    }}
                    onBlur={e=>{ if(e.target.value==='') setOrderData(p=>({...p,groupCount:1})) }}
                    placeholder="Number of people"/>
                  {Array.from({length:Number(orderData.groupCount)||1}).map((_,i)=>(
                    <input key={i} className="input-field" placeholder={`Person ${i+1} name`} value={orderData.groupNames[i]||''} onChange={e=>{const arr=[...orderData.groupNames];arr[i]=e.target.value;setOrderData(p=>({...p,groupNames:arr}))}} style={{fontSize:'0.82rem'}}/>
                  ))}
                </div>
              )}
            </div>
          )}
          <div>
            <label style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.72rem',fontWeight:700,color:'#6B6B6B',textTransform:'uppercase',letterSpacing:'0.04em'}}>Yards {asoEbi.min_yards?`(min. ${asoEbi.min_yards})`:''}</label>
            <input className="input-field" type="number" min={asoEbi.min_yards||1} value={orderData.yards} onChange={e=>setOrderData(p=>({...p,yards:e.target.value}))} style={{marginTop:'0.3rem'}}/>
          </div>
          {!isMore && collectionPoints.length>0 && (
            <div>
              <label style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.72rem',fontWeight:700,color:'#6B6B6B',textTransform:'uppercase',letterSpacing:'0.04em'}}>Collection Point</label>
              <select className="input-field" value={orderData.collectionPoint} onChange={e=>setOrderData(p=>({...p,collectionPoint:e.target.value}))} style={{marginTop:'0.3rem'}}>
                <option value="">Select a collection point</option>
                {collectionPoints.map(pt=><option key={pt.id} value={pt.name}>{pt.name}{pt.address?` — ${pt.address}`:''}</option>)}
              </select>
            </div>
          )}
          {totalAmount>0 && (
            <div style={{background:'rgba(155,175,155,0.08)',border:'1px solid rgba(155,175,155,0.2)',borderRadius:'0.875rem',padding:'0.875rem 1rem',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <span style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.82rem',color:'#6B6B6B'}}>Total Amount</span>
              <span style={{fontFamily:'Gibs, serif',fontWeight:400,fontSize:'1.3rem',color:'#1A1A1A'}}>{cs}{totalAmount.toLocaleString()}</span>
            </div>
          )}
          <button onClick={submitOrder} disabled={!orderData.name.trim()||!orderData.phone.trim()||submitting} style={{background:'linear-gradient(135deg,#C4826A,#F4B300)',color:'white',border:'none',borderRadius:100,padding:'0.875rem',cursor:'pointer',fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.875rem',opacity:(!orderData.name.trim()||!orderData.phone.trim())?0.5:1}}>
            {submitting ? (isMore ? 'Adding Yards…' : 'Submitting…') : (isMore ? 'Add Yards →' : 'Submit Order →')}
          </button>
        </div>
      </div>
    )
  }

  // ── Browse view ───────────────────────────────────────────
  const swatchPhotos = asoEbiPhotos.filter(p=>p.type==='swatch')
  const womenPhotos  = asoEbiPhotos.filter(p=>p.type==='style_women')
  const menPhotos    = asoEbiPhotos.filter(p=>p.type==='style_men')
  const doNotPhotos  = asoEbiPhotos.filter(p=>p.type==='do_not_sew')
  const coordinatorPhone = asoEbi.vendor_contact

  return (
    <div style={{display:'flex',flexDirection:'column',gap:'1.25rem',paddingBottom:'80px'}}>

      {/* Payment-confirmed banner (after returning from Paystack) */}
      {paidBanner && (
        <div style={{ background:'linear-gradient(135deg,#F0F5F0,#E8F0E8)', border:'1.5px solid rgba(155,175,155,0.45)', borderRadius:'1.25rem', padding:'1rem 1.25rem', display:'flex', alignItems:'center', gap:'0.625rem' }}>
          <span style={{ fontSize:'1.3rem' }}>✅</span>
          <div>
            <p style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.05rem', color:'#1A1A1A', margin:0 }}>Payment confirmed!</p>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#5A7A5A', margin:0 }}>Your Aso Ebi is paid for — the couple has been notified 💛</p>
          </div>
        </div>
      )}

      {/* Already ordered — running receipt of this guest's Aso Ebi purchase */}
      {existingOrder ? (
        <div style={{background:'linear-gradient(135deg,#F0F5F0,#E8F0E8)',border:'1.5px solid rgba(155,175,155,0.4)',borderRadius:'1.25rem',padding:'1.25rem 1.5rem'}}>
          <div style={{display:'flex',alignItems:'center',gap:'0.5rem',marginBottom:'0.75rem'}}>
            <div style={{width:28,height:28,background:'rgba(155,175,155,0.2)',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center'}}>
              <span style={{fontSize:'0.9rem'}}>{anyPaid ? '✓' : '🧵'}</span>
            </div>
            <p style={{fontFamily:'Gibs, serif',fontWeight:400,fontSize:'1.05rem',color:'#1A1A1A',margin:0}}>{anyPaid ? 'Your Aso Ebi Receipt' : 'Your Order'}</p>
          </div>

          {/* Accumulated receipt of everything paid for */}
          {anyPaid && (
            <>
              {[
                {label:'Yards', value:`${accYards} yard${accYards!==1?'s':''}`},
                {label:'Total Paid', value:`${cs}${accAmount.toLocaleString()}`},
                {label:'Collection', value: accPoints.length ? accPoints.join(', ') : 'To be confirmed'},
              ].map(r=>(
                <div key={r.label} style={{display:'flex',gap:'0.5rem',marginBottom:'0.25rem'}}>
                  <span style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.78rem',color:'#6B9B6B',minWidth:90}}>{r.label}</span>
                  <span style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.78rem',color:'#1A1A1A',fontWeight:700}}>{r.value}</span>
                </div>
              ))}
              <div style={{display:'inline-flex',alignItems:'center',gap:'0.3rem',marginTop:'0.5rem',background:'rgba(155,175,155,0.18)',border:'1px solid rgba(155,175,155,0.35)',borderRadius:100,padding:'0.2rem 0.7rem'}}>
                <span style={{fontSize:'0.7rem'}}>✅</span>
                <span style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.7rem',fontWeight:700,color:'#5A7A5A'}}>Paid{paidPurchases.length>1?` · ${paidPurchases.length} orders`:''}</span>
              </div>
            </>
          )}

          {/* An order still awaiting payment (a fresh one, or "order more") */}
          {pendingPurchase && (
            <div style={{ marginTop:'0.875rem' }}>
              {giftsOn
                ? <OnlinePayBlock amount={pendingPurchase.amount} orderRef={pendingPurchase.ref}/>
                : <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.78rem',color:'#B36B55',margin:0,fontWeight:600}}>You have an order ({pendingPurchase.yards} yards) awaiting payment.</p>}
            </div>
          )}

          <div style={{display:'flex',gap:'0.5rem',marginTop:'0.875rem',flexWrap:'wrap'}}>
            <button onClick={()=>{ if(existingOrder.ref) loadFullOrder(existingOrder.ref); else setShowOrderDetails(true) }} style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.78rem',fontWeight:700,color:'#5A7A5A',background:'rgba(155,175,155,0.12)',border:'1px solid rgba(155,175,155,0.3)',borderRadius:100,padding:'0.5rem 1.125rem',cursor:'pointer'}}>
              View Order Details →
            </button>
            {!deadlinePassed && (
              <button onClick={()=>{ setReceipt(null); setOrderData({ name:existingOrder.name||'', phone:existingOrder.phone||'', yards:asoEbi?.min_yards||1, isGroup:false, groupCount:1, groupNames:[], collectionPoint:'' }); setStep('order') }} style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.78rem',fontWeight:700,color:'#C4826A',background:'transparent',border:'1.5px solid rgba(196,130,106,0.5)',borderRadius:100,padding:'0.5rem 1.125rem',cursor:'pointer'}}>
                + Order More Yards
              </button>
            )}
          </div>
          {deadlinePassed && <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.7rem',color:'#BCBCBC',margin:'0.5rem 0 0'}}>Order deadline has passed</p>}
        </div>
      ) : (
        /* Urgency card */
        daysLeft !== null && daysLeft > 0 ? (
          <div style={{background:'linear-gradient(135deg,#FAF5EC,#F5EAD8)',border:'1px solid rgba(196,130,106,0.25)',borderRadius:'1.25rem',padding:'1.25rem 1.5rem',textAlign:'center'}}>
            <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.65rem',fontWeight:700,color:'#C4826A',letterSpacing:'0.15em',textTransform:'uppercase',margin:'0 0 0.5rem'}}>🧵 Aso Ebi Orders Close In</p>
            <p style={{fontFamily:'Gibs, serif',fontWeight:400,fontSize:'2.5rem',color:'#1A1A1A',margin:'0 0 0.25rem',lineHeight:1}}>{daysLeft}</p>
            <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.75rem',color:'#9B9B9B',margin:'0 0 0.5rem'}}>days remaining · Order by {new Date(asoEbi.order_deadline).toLocaleDateString('en-GB',{day:'numeric',month:'long'})}</p>
            {orderCount > 0 && <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.78rem',color:'#C4826A',fontWeight:600,margin:'0 0 0.875rem'}}>{orderCount} guest{orderCount!==1?'s have':' has'} already ordered</p>}
            {!isClosed && <button onClick={()=>setStep('order')} style={{background:'linear-gradient(135deg,#C4826A,#F4B300)',color:'white',border:'none',borderRadius:100,padding:'0.7rem 1.75rem',cursor:'pointer',fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.875rem'}}>Order My Yards →</button>}
          </div>
        ) : isClosed ? (
          <div style={{background:'rgba(196,130,106,0.06)',border:'1px solid rgba(196,130,106,0.15)',borderRadius:'1.25rem',padding:'1rem',textAlign:'center'}}>
            <p style={{fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.82rem',color:'#C4826A',margin:0}}>Aso Ebi orders are now closed.</p>
          </div>
        ) : (
          <button onClick={()=>setStep('order')} style={{background:'linear-gradient(135deg,#C4826A,#F4B300)',color:'white',border:'none',borderRadius:100,padding:'0.875rem',cursor:'pointer',fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.875rem'}}>Order My Yards →</button>
        )
      )}

      {/* Fabric info + swatch side by side */}
      <div className="card" style={{padding:'1.25rem 1.5rem'}}>
        <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.65rem',fontWeight:700,color:'#C4826A',letterSpacing:'0.12em',textTransform:'uppercase',margin:'0 0 0.875rem'}}>The Fabric</p>
        <div style={{display:'flex',gap:'1rem',alignItems:'flex-start'}}>
          {swatchPhotos.length>0 && (
            <div onClick={()=>setLightbox({url:swatchPhotos[0].url,label:'Fabric Swatch'})} style={{width:110,minWidth:110,height:140,borderRadius:'0.875rem',overflow:'hidden',cursor:'pointer',flexShrink:0}}>
              <img src={swatchPhotos[0].url} alt="Fabric Swatch" style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}}/>
            </div>
          )}
          <div style={{flex:1}}>
            <h2 style={{fontFamily:'Gibs, serif',fontWeight:400,fontSize:'1.2rem',color:'#1A1A1A',margin:'0 0 0.625rem'}}>{asoEbi.fabric_name||'Coming soon'}</h2>
            {[
              {label:'Vendor', value:asoEbi.vendor_name},
              {label:'Price', value:asoEbi.price_per_yard?`${cs}${Number(asoEbi.price_per_yard).toLocaleString()} per yard`:null},
              {label:'Minimum', value:asoEbi.min_yards?`${asoEbi.min_yards} yards per person`:null},
            ].filter(r=>r.value).map(r=>(
              <div key={r.label} style={{display:'flex',gap:'0.4rem',marginBottom:'0.3rem'}}>
                <span style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.75rem',color:'#9B9B9B',minWidth:60}}>{r.label}</span>
                <span style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.75rem',color:'#1A1A1A',fontWeight:600}}>{r.value}</span>
              </div>
            ))}
          </div>
        </div>
        {/* Contact block */}
        {(asoEbi.vendor_name||coordinatorPhone) && (
          <div style={{marginTop:'1rem',paddingTop:'0.875rem',borderTop:'1px solid #F3F0EA'}}>
            <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.72rem',color:'#9B9B9B',margin:'0 0 0.3rem'}}>Have a question about the fabric?</p>
            {asoEbi.vendor_name && <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.82rem',fontWeight:700,color:'#1A1A1A',margin:'0 0 0.5rem'}}>Contact {asoEbi.vendor_name}</p>}
            {coordinatorPhone && (
              <div style={{display:'flex',gap:'0.5rem',flexWrap:'wrap'}}>
                <a href={`tel:${coordinatorPhone}`} style={{display:'flex',alignItems:'center',gap:'0.35rem',background:'rgba(155,175,155,0.1)',border:'1px solid rgba(155,175,155,0.25)',borderRadius:100,padding:'0.45rem 0.875rem',textDecoration:'none',fontFamily:'Nunito Sans, sans-serif',fontSize:'0.75rem',fontWeight:700,color:'#5A7A5A'}}>📞 Call</a>
                <a href={`https://wa.me/${coordinatorPhone.replace(/[\s\-+()]/g,'').replace(/^0/,'234')}?text=${encodeURIComponent(`Hi ${asoEbi.vendor_name||'there'}, I have a question about the Aso Ebi for ${wedding.partner1_name} & ${wedding.partner2_name}'s wedding.`)}`} target="_blank" rel="noreferrer" style={{display:'flex',alignItems:'center',gap:'0.35rem',background:'rgba(37,211,102,0.1)',border:'1px solid rgba(37,211,102,0.25)',borderRadius:100,padding:'0.45rem 0.875rem',textDecoration:'none',fontFamily:'Nunito Sans, sans-serif',fontSize:'0.75rem',fontWeight:700,color:'#1A7A3A'}}>💬 WhatsApp</a>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Style photos with lightbox */}
      {[
        {photos:womenPhotos, label:"Women's Styles"},
        {photos:menPhotos,   label:"Men's Styles"},
      ].filter(s=>s.photos.length>0).map(s=>(
        <div key={s.label} className="card" style={{padding:'1.25rem 1.5rem'}}>
          <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.65rem',fontWeight:700,color:'#C4826A',letterSpacing:'0.12em',textTransform:'uppercase',margin:'0 0 0.875rem'}}>{s.label}</p>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))',gap:'0.625rem'}}>
            {s.photos.map(p=>(
              <div key={p.id} onClick={()=>setLightbox({url:p.url,label:s.label})} style={{cursor:'pointer',borderRadius:'0.75rem',overflow:'hidden',aspectRatio:'4/5',transition:'transform 0.15s'}} onMouseEnter={e=>e.currentTarget.style.transform='scale(1.02)'} onMouseLeave={e=>e.currentTarget.style.transform='scale(1)'}>
                <img src={p.url} alt={s.label} style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}}/>
              </div>
            ))}
          </div>
          <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.7rem',color:'#BCBCBC',margin:'0.625rem 0 0',textAlign:'center'}}>Tap any photo to enlarge for your tailor</p>
        </div>
      ))}

      {/* Do Not Sew */}
      {doNotPhotos.length>0 && (
        <div className="card" style={{padding:'1.25rem 1.5rem',border:'1.5px solid rgba(28,20,16,0.15)'}}>
          <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.65rem',fontWeight:700,color:'#1A1A1A',letterSpacing:'0.12em',textTransform:'uppercase',margin:'0 0 0.5rem'}}>Do Not Sew ✕</p>
          <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.75rem',color:'#9B9B9B',margin:'0 0 0.875rem'}}>The couple has asked guests not to wear these styles.</p>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))',gap:'0.625rem'}}>
            {doNotPhotos.map(p=>(
              <div key={p.id} onClick={()=>setLightbox({url:p.url,label:'Avoid this style'})} style={{position:'relative',borderRadius:'0.75rem',overflow:'hidden',aspectRatio:'4/5',cursor:'pointer'}}>
                <img src={p.url} alt="Do not wear" style={{width:'100%',height:'100%',objectFit:'cover',filter:'grayscale(0.4)'}}/>
                <div style={{position:'absolute',inset:0,background:'rgba(28,20,16,0.5)',display:'flex',alignItems:'center',justifyContent:'center'}}><span style={{fontSize:'2.5rem',color:'rgba(255,100,100,0.9)',fontWeight:900}}>✕</span></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sticky bottom CTA */}
      {!isClosed && !existingOrder && (
        <div style={{position:'fixed',bottom:0,left:0,right:0,zIndex:50,background:'white',borderTop:'1px solid #E5E0D8',padding:'0.875rem 1rem 0.875rem',display:'flex',alignItems:'center',justifyContent:'space-between',gap:'0.75rem',boxShadow:'0 -4px 16px rgba(0,0,0,0.06)'}}>
          <div style={{minWidth:0}}>
            <p style={{fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.85rem',color:'#1A1A1A',margin:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{asoEbi.fabric_name||'Fabric'}</p>
            <p style={{fontFamily:'Nunito Sans, sans-serif',fontSize:'0.72rem',color:'#9B9B9B',margin:0}}>{asoEbi.price_per_yard?`${cs}${Number(asoEbi.price_per_yard).toLocaleString()}/yard`:''}{ asoEbi.min_yards?` · Min ${asoEbi.min_yards} yards`:''}</p>
          </div>
          <button onClick={()=>setStep('order')} style={{background:'linear-gradient(135deg,#C4826A,#F4B300)',color:'white',border:'none',borderRadius:100,padding:'0.7rem 1.25rem',cursor:'pointer',fontFamily:'Nunito Sans, sans-serif',fontWeight:700,fontSize:'0.82rem',whiteSpace:'nowrap',flexShrink:0}}>
            Order My Yards →
          </button>
        </div>
      )}
    </div>
  )
}

